import React, { useEffect, useState } from "react";
import { AccountInfo } from '@azure/msal-browser';
import Text from "@ingka/text";
import 'react-datepicker/dist/react-datepicker.css'
import 'react-datepicker/dist/react-datepicker-cssmodules.css'
import '@ingka/svg-icon/style.scss';
import '@ingka/button/style.scss';
import '@ingka/forms/style.scss';
import '@ingka/focus/style.scss';
import '@ingka/text/style.scss';
import Checkbox from '@ingka/checkbox';
import '../score-access.scss';
import { ProductInputSection } from "./product-input-section";
import { DateInputSection } from "./time-and-place-input-section";
import { countries, scoreTypesGroup } from "../constants";
import Button from "@ingka/button";
import siteUrl from "../models/url-routes";
import { validIkeaNos } from "../constants";
import { CampaignNameInput } from "./campaign-name-input";
import "../../app.scss"
import { PPSTemplate } from "./pps-template";


interface OptionProps {
  accountInfo: AccountInfo | null
}

export const PPSScoreAccess: React.FC<OptionProps> = ({accountInfo}) => {
  const [ campaignName, setCampaignName ] = useState<string>('')
  const [ campaignNameValidFlag, setCampaignNameValidFlag ] = useState<boolean>(true)
  const [ plannedCampaignNoString, setPlannedCampaignNo ] = useState<string>('')
  const [ validCampaignNos, setValidCampaignNos ] = useState<string[]>([])
  const [ scoreType, setScoreType ] = useState<string>('')
  const [ currentCountry, setCurrentCountry ] = useState('')
  const [ campaignInputValidFlag, setCampaignInputValidFlag ] = useState<boolean>(true)
  const [ exportButtonDisabled, setExportButtonDisabled ] = useState(true)
  const [ runId, setRunId ] = useState("")
  const [ exportToSalesForce, setExportToSalesForce ] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('');

  const handleExportButtonClicked = () => {
        // TODO input process all campaign numbers
        fetch(`${siteUrl}/pps/ppsrequest`,
          {
            method: "post",
            headers: {
                "Content-Type": "application/json",
                'Accept':'application/json'
            },
            body: JSON.stringify({
              author_name: accountInfo?.name,
              campaign_id: campaignName,
              product_hierarchy_type: scoreType.toLowerCase(),
              product_hierarchy_nos: plannedCampaignNoString,
              ru_code: countries[currentCountry].toLowerCase(),
              exportToSalesforce: exportToSalesForce
            })
        })
        .then(async response => {
          const runId = await response.text()
          if (runId) {
            setRunId(runId)
            setErrorMessage('');
          } else {
            throw new Error('Empty runId returned');
          }
        })
        .catch(error => {
          console.error('Error occured calling dyclss-backend:', error);
          setErrorMessage('An error occurred while processing your request. Please try again later.');
      });
  }

  useEffect(() => {
    const campaignNos = plannedCampaignNoString.replace(/\s/g, '').split(",")
    if (scoreType !== '') {
      const validNos = validIkeaNos[scoreType];
      const isValid = campaignNos.every((no: string) => validNos.includes(no));

      setCampaignInputValidFlag(isValid)
      setValidCampaignNos(validIkeaNos[scoreType])
    }
  },[scoreType, plannedCampaignNoString])

  useEffect(() => {
    const regex = /^[A-Za-z][A-Za-z0-9]*$/
    const isValid = regex.test(campaignName)

    setCampaignNameValidFlag(isValid)
  }, [campaignName])
    
  // Check required fields to enable export button
  useEffect(() => {
    const isValid = 
      campaignNameValidFlag &&
      campaignInputValidFlag &&
      currentCountry != "" &&
      (scoreType != '')
    setExportButtonDisabled(!isValid)
  }, [scoreType, campaignInputValidFlag, campaignNameValidFlag, currentCountry])

  const handleCampaignNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault()
    setCampaignName(e.target.value)
  }

  const handleScoreTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setScoreType(e.target.value)
  }

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCurrentCountry(e.target.value)
  }

  const handleExistingProductChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    event.preventDefault()
    const numberString = event.target.value
    setPlannedCampaignNo(numberString)
  }

  const handleExportToSalesForce = () => {
    setExportToSalesForce(!exportToSalesForce)
  }
  
  return (
    <PPSTemplate>
      <div className="score-container">
      <Text tagName="h2" textStyle="Heading.M">
        Product Propensity Score (PPS) access
      </Text>
      <Text tagName="p" textStyle="Statement.M">
        Product propensity scores are probabilities quantifying individual customer's interest in a particular
        product or product area. 
        <br/>
        <br/>
        The scores are available at product area (pa) or product range area (pra) and home furnishing business (hfb). Please 
        specify the pa_no/pra_no/hfb_no that will be highlighted in your campaign.
        <br/>
        <br/>
        You cannot combine codes from different categories such as pa_no with pra_no or hfb_no, etc. In the case of pa or pra, 
        they would be entered in the first field (the list of existing pa_no/pra_no/hfb_no).
        <br/>
        <br/>
        All codes within the IKEA catalog can be included.

      </Text>
      <CampaignNameInput 
        campaignName={campaignName}
        campaignNameValidFlag={campaignNameValidFlag}
        handleCampaignNameChange={handleCampaignNameChange}
      />
      <ProductInputSection
        plannedCampaignNos={plannedCampaignNoString}
        campaignInputValidFlag={campaignInputValidFlag}
        handleScoreTypeChange={handleScoreTypeChange}
        scoreGroups={scoreTypesGroup}
        validCampaignNos={validCampaignNos}
        handleExistingProductChange={handleExistingProductChange}
      />
      <DateInputSection
        countries={countries}
        currentCountry={currentCountry}
        handleCountryChange={handleCountryChange}
      />
      <div className='scenario-section'>
          <label className='option-label'>Export Results to Salesforce</label>
          <Checkbox 
            id='include-direct-id'
            name='includeDirect'
            value='include'
            checked={exportToSalesForce}
            onClick={handleExportToSalesForce}
          />
      </div>
      <div className="bottom-buttons-container">
        <div className="button-container-right">
            <Button size="medium" href="#/" type="secondary">
                Cancel
            </Button>
            <Button 
              size="medium" 
              type="emphasised" 
              onClick={handleExportButtonClicked}
              disabled={exportButtonDisabled}
            >
              Export results
            </Button>
        </div>
          <Text tagName="p" textStyle="Statement.M">
            {runId && !errorMessage ? `Your run ID for accessing data is ${runId}` : errorMessage}
          </Text>
      </div>
      </div>
    </PPSTemplate>
  )
}